import React, { useState } from "react";
import {
  Box,
  Link,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Container,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useLocation, useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BASE_URL from "../../config/apiConfig";
import { saveUserToken } from "../../utils/auth";

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function UserLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    setEmailError("");
    setPasswordError("");

    // Validate email format
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/user/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Login success:", data);
        saveUserToken(data.token, data.user.email);

        const redirectPath = location.state?.redirectTo;

        if (redirectPath) {
          navigate(redirectPath);
        } else {
          navigate("/user/home"); // default fallback
        }
      } else {
        const data = await response.json();
        setEmailError(data.message);
        // if (data.message === "User not found") {
        //   setEmailError("User not found");
        // } else if (data.message === "Invalid email or password") {
        //   setPasswordError("Invalid email or password");
        // } else {
        //   setPasswordError("An unexpected error occurred");
        // }
      }
    } catch (error) {
      console.error("Error during login:", error);
      setPasswordError("Something went wrong, please try again");
    } finally {
      setLoading(false);
    }
  };

  const renderForm = (
    <Box
      component="form"
      onSubmit={handleSignIn}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      sx={{ width: "100%", maxWidth: "400px", mb: 2 }}
    >
      <TextField
        fullWidth
        name="email"
        label="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!emailError}
        sx={{
          mb: 2,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
      />

      <TextField
        fullWidth
        name="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type={showPassword ? "text" : "password"}
        error={!!passwordError}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
      />

      {/* Centered Error Message */}
      {(emailError || passwordError) && (
        <Typography
          variant="body2"
          color="error"
          sx={{ textAlign: "center", mb: 2 }}
        >
          {emailError || passwordError}
        </Typography>
      )}

      <Box sx={{ textAlign: "right", mb: 2 }}>
        <Link
          href="/recoverpassword"
          variant="body2"
          color="inherit"
          sx={{
            fontSize: "0.9rem",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline", color: "#51833a" },
          }}
        >
          Forgot password?
        </Link>
      </Box>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="inherit"
        variant="contained"
        loading={loading}
        sx={{
          background: "linear-gradient(45deg, #51833a 30%, #66bb6a 90%)",
          color: "#fff",
          "&:hover": { backgroundColor: "#406027" },
          mb: 2,
          textTransform: "none",
          minHeight: "50px",
          borderRadius: "8px",
          transition: "0.3s ease",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        Sign in
      </LoadingButton>
    </Box>
  );

  return (
    <Container
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: { xs: 2, md: 0 },
        background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)", // Gradient background
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          minHeight: "70vh",
          borderRadius: "16px",
          boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: "900px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            padding: { xs: 3, md: 5 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            backgroundColor: "#f9fafb",
          }}
        >
          <Box
            gap={1.5}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            sx={{ mb: 5 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#333",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Sign in
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Don’t have an account?{" "}
              <Link
                href="/signup"
                variant="body2"
                sx={{
                  textDecoration: "none",
                  color: "#51833a",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Sign Up
              </Link>
            </Typography>
          </Box>

          {renderForm}
        </Box>

        {/* Right Side Illustration */}
        <Box
          sx={{
            backgroundColor: "#e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            padding: { xs: 2, md: 4 },
            position: "relative",
          }}
        >
          <Box
            component="img"
            src="/Images/Login_Body.webp"
            alt="Task illustration"
            sx={{ width: "100%", maxWidth: "600px", borderRadius: "12px" }}
          />
          {/* Decorative Shape */}
          <Box
            sx={{
              width: "120px",
              height: "120px",
              backgroundColor: "#51833a",
              borderRadius: "50%",
              position: "absolute",
              bottom: "-20px",
              right: "-20px",
              opacity: 0.7,
              zIndex: -1,
            }}
          />
        </Box>
      </Box>
    </Container>
  );
}

export default UserLogin;
